import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { developers as devApi } from '@/api/developers';
import { projects as projectsApi } from '@/api/projects';
import type {
  DeveloperInvolvementInput,
  DeveloperLevel,
  DeveloperUpsertRequest,
  DeveloperView,
  ProjectInvolvement,
  ProjectView,
  UUID,
} from '@/api/types';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import EmptyState from '@/components/EmptyState';
import Pagination from '@/components/Pagination';
import { useConfirm } from '@/components/ConfirmDialog';
import { useTablePageSize } from '@/lib/useTablePageSize';

const LEVELS: DeveloperLevel[] = ['JUNIOR', 'MIDDLE', 'SENIOR', 'LEAD', 'PRINCIPAL'];
const INVOLVEMENTS: ProjectInvolvement[] = ['NEW', 'OCCASIONAL', 'REGULAR', 'CORE', 'OWNER'];

function repoDisplayName(fullName: string): string {
  const idx = fullName.indexOf('/');
  return idx === -1 ? fullName : fullName.slice(idx + 1);
}

export default function DevelopersPage() {
  const { id: orgId } = useRequireOrg();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<DeveloperView | null>(null);
  const [creating, setCreating] = useState(false);
  const [pageSize, setPageSize] = useTablePageSize('developers', 20);
  const [page, setPage] = useState(0);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['developers', orgId],
    queryFn: () => devApi.list(orgId),
  });

  const deleteMutation = useMutation({
    mutationFn: (login: string) => devApi.delete(orgId, login),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['developers', orgId] }),
  });

  const totalPages = Math.max(1, Math.ceil((data?.length ?? 0) / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const visible = (data ?? []).slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <>
      <PageHeader
        title="Developers"
        subtitle="Profiles drive how the AI weighs effort estimates (level + per-project involvement)."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + Add developer
          </button>
        }
      />

      {isLoading ? <Spinner label="Loading developers…" /> : null}
      {error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}

      {data && data.length === 0 ? (
        <EmptyState
          title="No developer profiles yet"
          description="Profiles are created automatically when we see a new PR author. You can edit a profile to refine the AI's estimates."
        />
      ) : null}

      {data && data.length > 0 ? (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>GitHub login</th>
                  <th>Level</th>
                  <th>Projects configured</th>
                  <th>Years of exp.</th>
                  <th>Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map(d => (
                  <tr key={d.githubLogin}>
                    <td>@{d.githubLogin}</td>
                    <td>{d.level ?? '—'}</td>
                    <td>
                      {d.involvements.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <span className="badge badge-info">{d.involvements.length}</span>
                      )}
                    </td>
                    <td>{d.yearsOfExperience ?? '—'}</td>
                    <td className="notes-cell">{d.notes ?? '—'}</td>
                    <td className="actions">
                      <button className="btn btn-ghost" onClick={() => setEditing(d)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-danger"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete developer',
                            message: `Delete profile for @${d.githubLogin}?`,
                            confirmLabel: 'Delete',
                            danger: true,
                          });
                          if (ok) deleteMutation.mutate(d.githubLogin);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPrev={() => setPage(p => Math.max(0, p - 1))}
            onNext={() => setPage(p => p + 1)}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}

      {creating ? (
        <DeveloperFormDialog
          title="Add developer"
          initialLogin=""
          initial={emptyRequest()}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <DeveloperFormDialog
          title={`Edit @${editing.githubLogin}`}
          initialLogin={editing.githubLogin}
          loginLocked
          initial={{
            level: editing.level,
            yearsOfExperience: editing.yearsOfExperience,
            notes: editing.notes,
            jiraAccountId: editing.jiraAccountId,
            involvements: editing.involvements.map(i => ({
              projectId: i.projectId,
              involvement: i.involvement,
            })),
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function emptyRequest(): DeveloperUpsertRequest {
  return {
    level: null,
    yearsOfExperience: null,
    notes: null,
    jiraAccountId: null,
    involvements: [],
  };
}

function DeveloperFormDialog({
  title,
  initialLogin,
  loginLocked,
  initial,
  onClose,
}: {
  title: string;
  initialLogin: string;
  loginLocked?: boolean;
  initial: DeveloperUpsertRequest;
  onClose: () => void;
}) {
  const { id: orgId } = useRequireOrg();
  const qc = useQueryClient();
  const [login, setLogin] = useState(initialLogin);
  const [form, setForm] = useState<DeveloperUpsertRequest>(initial);

  const { data: projects } = useQuery({
    queryKey: ['projects', orgId],
    queryFn: () => projectsApi.list(orgId),
  });

  const upsert = useMutation({
    mutationFn: () => devApi.upsert(orgId, login.trim(), form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['developers', orgId] });
      onClose();
    },
  });

  const involvementByProject = useMemo(() => {
    const map = new Map<UUID, ProjectInvolvement>();
    for (const inv of form.involvements) map.set(inv.projectId, inv.involvement);
    return map;
  }, [form.involvements]);

  const setProjectInvolvement = (projectId: UUID, value: ProjectInvolvement | '') => {
    setForm(f => {
      const next: DeveloperInvolvementInput[] = f.involvements.filter(i => i.projectId !== projectId);
      if (value) next.push({ projectId, involvement: value });
      return { ...f, involvements: next };
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-split" onClick={e => e.stopPropagation()}>
        <h3>{title}</h3>
        <div className="form-grid">
          <label>
            <span className="muted small">GitHub login</span>
            <input
              className="input"
              value={login}
              onChange={e => setLogin(e.target.value)}
              disabled={loginLocked}
              placeholder="octocat"
            />
          </label>
          <label>
            <span className="muted small">Level</span>
            <select
              className="select"
              value={form.level ?? ''}
              onChange={e =>
                setForm(f => ({ ...f, level: (e.target.value || null) as DeveloperLevel | null }))
              }
            >
              <option value="">—</option>
              {LEVELS.map(l => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Years of experience</span>
            <input
              className="input"
              type="number"
              min={0}
              value={form.yearsOfExperience ?? ''}
              onChange={e =>
                setForm(f => ({
                  ...f,
                  yearsOfExperience: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
            />
          </label>
          <label className="full">
            <span className="muted small">Notes</span>
            <textarea
              className="input"
              rows={3}
              value={form.notes ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value || null }))}
              placeholder="Context the AI should know about this contributor."
            />
          </label>
          <label className="full">
            <span className="muted small">
              Jira account id{' '}
              <span className="muted small">
                — find under your Atlassian profile URL (e.g. `557058:abc...`). When set,
                Jira worklog sync filters to only this developer's logs; otherwise the full
                issue worklog is summed.
              </span>
            </span>
            <input
              className="input"
              value={form.jiraAccountId ?? ''}
              onChange={e => setForm(f => ({ ...f, jiraAccountId: e.target.value || null }))}
              placeholder="557058:f58131cb-..."
            />
          </label>
        </div>

        <div className="modal-scroll">
          <span className="muted small">Project involvement</span>
          <ProjectInvolvementEditor
            projects={projects ?? []}
            involvementByProject={involvementByProject}
            onChange={setProjectInvolvement}
          />
        </div>

        {upsert.error ? <ErrorBanner error={upsert.error} /> : null}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!login.trim() || upsert.isPending}
            onClick={() => upsert.mutate()}
          >
            {upsert.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectInvolvementEditor({
  projects,
  involvementByProject,
  onChange,
}: {
  projects: ProjectView[];
  involvementByProject: Map<UUID, ProjectInvolvement>;
  onChange: (projectId: UUID, value: ProjectInvolvement | '') => void;
}) {
  if (projects.length === 0) {
    return (
      <p className="muted small" style={{ marginTop: '0.4rem' }}>
        No projects yet — they appear automatically once we've analyzed a PR for them.
      </p>
    );
  }
  return (
    <div className="table-scroll" style={{ marginTop: '0.4rem' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Project</th>
            <th style={{ width: 200 }}>Involvement</th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => (
            <tr key={p.id}>
              <td>{repoDisplayName(p.fullName)}</td>
              <td>
                <select
                  className="select"
                  value={involvementByProject.get(p.id) ?? ''}
                  onChange={e => onChange(p.id, (e.target.value || '') as ProjectInvolvement | '')}
                >
                  <option value="">—</option>
                  {INVOLVEMENTS.map(i => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
